/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview PaymentModal MethodStep step UI.
 * @module modals/PaymentModal/steps/PaymentModalMethodStep
 */

import { Trans } from "react-i18next";
import CircularProgress from "@mui/material/CircularProgress";
import { ReactComponent as CashImage } from "../../../assets/icons/PaymentModal/CashImage.svg";
import { ReactComponent as CardImage } from "../../../assets/icons/PaymentModal/CardImage.svg";
import { ReactComponent as PaymentsIcon } from "../../../assets/icons/PaymentModal/PaymentsIcon.svg";
import {
  detectCardType,
  formatCardNumber,
  formatExpiryDate,
  validateExpiryDate,
} from "../../../utils/payment/cardValidation.js";
import PaymentConfirmPhoneField from "../PaymentConfirmPhoneField.jsx";

const PaymentModalMethodStep = ({
  styles,
  t,
  paymentMethod,
  setPaymentMethod,
  wantsInvoiceCash,
  handleWantsInvoiceChange,
  countryCodeOptions,
  phoneCountryCodeCash,
  phoneCountryCodeCard,
  confirmPhoneCash,
  confirmPhoneCard,
  setPhoneCountryCodeCash,
  setPhoneCountryCodeCard,
  handleConfirmPhoneChange,
  getFieldValidationState,
  getErrorMessage,
  formData,
  handleInputChange,
  cardType,
  setCardType,
  cardValidationError,
  setCardValidationError,
  expiryValidationError,
  setExpiryValidationError,
  wantsInvoiceCard,
}) => (
          <div className={styles.stepContent}>
            {/* Payment Method Selection */}
            <div className={styles.formRow}>
              <div className={styles.formColumn}>
                <div className={styles.paymentMethodButtons}>
                  <button
                    className={`${styles.paymentMethodButton} ${
                      paymentMethod === "cash" ? styles.activeButton : ""
                    }`}
                    onClick={() => setPaymentMethod("cash")}
                  >
                    {t("components.paymentModal.cashSection1Title")}
                  </button>
                  <button
                    className={`${styles.paymentMethodButton} ${
                      paymentMethod === "card" ? styles.activeButton : ""
                    }`}
                    onClick={() => setPaymentMethod("card")}
                  >
                    {t("components.paymentModal.paymentMethodCard")}
                  </button>
                </div>
              </div>
            </div>

            {/* Cash Payment Content */}
            {paymentMethod === "cash" && (
              <div className={styles.cashPaymentContent}>
                <div className={styles.cashPaymentTitleContainer}>
                  <div className={styles.cashPaymentTitle}>
                    {t("components.paymentModal.cashSection1Title")}
                  </div>
                  <p className={styles.cashPaymentSubtitle}>
                    {t("components.paymentModal.cashSection1Description")}
                  </p>
                </div>
                <div className={styles.cashPaymentInstructions}>
                  <p className={styles.cashPaymentInstruction}>
                    1.{" "}
                    <Trans
                      i18nKey="components.paymentModal.cashSection1Instruction1"
                      components={{ bold: <strong /> }}
                    />
                  </p>
                  <p className={styles.cashPaymentInstruction}>
                    2. {t("components.paymentModal.cashSection1Instruction2")}
                  </p>
                  <p className={styles.cashPaymentInstruction}>
                    3. {t("components.paymentModal.cashSection1Instruction3")}
                  </p>
                </div>
                <div className={styles.cashPaymentInvoiceSection}>
                  <div className={styles.invoiceCheckboxContainer}>
                    <input
                      type="checkbox"
                      id="wantsInvoiceCash"
                      className={styles.invoiceCheckbox}
                      checked={wantsInvoiceCash}
                      onChange={(e) =>
                        handleWantsInvoiceChange("cash", e.target.checked)
                      }
                    />
                    <label
                      htmlFor="wantsInvoiceCash"
                      className={styles.invoiceCheckboxLabel}
                    >
                      {t("components.paymentModal.cashSection1GenerateInvoice")}
                    </label>
                  </div>
                  {wantsInvoiceCash && (
                    <div className={styles.categoryContainer}>
                      <PaymentConfirmPhoneField
                        labelClassName={styles.label}
                        method="cash"
                        t={t}
                        countryCodeOptions={countryCodeOptions}
                        phoneCountryCode={phoneCountryCodeCash}
                        confirmPhone={confirmPhoneCash}
                        validationField="confirmPhoneCash"
                        onCountryCodeChange={setPhoneCountryCodeCash}
                        onConfirmPhoneChange={handleConfirmPhoneChange}
                        getFieldValidationState={getFieldValidationState}
                        getErrorMessage={getErrorMessage}
                      />
                    </div>
                  )}
                </div>
                <CashImage className={styles.cashPaymentImage} />
              </div>
            )}

            {/* Card Payment Content */}
            {paymentMethod === "card" && (
              <div className={styles.cardPaymentContent}>
                <div className={styles.cardPaymentTitleContainer}>
                  <div className={styles.cardPaymentTitle}>
                    {t("components.paymentModal.cardSection1Title")}
                  </div>
                  <p className={styles.cardPaymentSubtitle}>
                    {t("components.paymentModal.cardSection1Description")}
                  </p>
                </div>

                <div className={styles.cardPaymentForm}>
                  {/* First Row: Cardholder Name and Card Number */}
                  <div className={styles.formRow}>
                    <div className={styles.formColumn}>
                      <div className={styles.formLabel}>
                        {t("components.paymentModal.cardSection1Label1")}
                      </div>
                      <input
                        type="text"
                        value={formData.nombreTitular}
                        onChange={(e) =>
                          handleInputChange("nombreTitular", e.target.value)
                        }
                        className={styles.formInput}
                        placeholder={t(
                          "components.paymentModal.cardSection1Placeholder1",
                        )}
                      />
                    </div>
                    <div className={styles.formColumn}>
                      <div className={styles.formLabel}>
                        {t("components.paymentModal.cardSection1Label2")}
                      </div>
                      <div className={styles.cardInputContainer}>
                        <input
                          type="text"
                          value={formData.numeroTarjeta}
                          onChange={(e) =>
                            handleInputChange("numeroTarjeta", e.target.value)
                          }
                          className={styles.cardInput}
                          placeholder={t(
                            "components.paymentModal.cardSection1Placeholder2",
                          )}
                          maxLength="19"
                        />
                        {cardType && (
                          <div
                            className={`${styles.cardType} ${styles[cardType]}`}
                          >
                            {cardType.toUpperCase()}
                          </div>
                        )}
                      </div>
                      {cardValidationError && (
                        <div className={styles.errorMessage}>
                          {cardValidationError}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Second Row: Expiry Date and CVV */}
                  <div className={styles.formRow}>
                    <div className={styles.formColumn}>
                      <div className={styles.formLabel}>
                        {t("components.paymentModal.cardSection1Label3")}
                      </div>
                      <div className={styles.expiryContainer}>
                        <input
                          type="text"
                          value={formData.fechaDeVencimiento}
                          onChange={(e) =>
                            handleInputChange(
                              "fechaDeVencimiento",
                              e.target.value,
                            )
                          }
                          className={styles.expiryInput}
                          placeholder={t(
                            "components.paymentModal.cardSection1Placeholder3",
                          )}
                          maxLength="5"
                        />
                      </div>
                      {expiryValidationError && (
                        <div className={styles.errorMessage}>
                          {expiryValidationError}
                        </div>
                      )}
                    </div>
                    <div className={styles.formColumn}>
                      <div className={styles.formLabel}>
                        {t("components.paymentModal.cardSection1Label4")}
                      </div>
                      <div className={styles.cvvContainer}>
                        <input
                          type="text"
                          value={formData.codigoDeSeguridad}
                          onChange={(e) =>
                            handleInputChange(
                              "codigoDeSeguridad",
                              e.target.value,
                            )
                          }
                          className={styles.cvvInput}
                          placeholder={t(
                            "components.paymentModal.cardSection1Placeholder4",
                          )}
                          maxLength="4"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Third Row: Invoice checkbox and optional phone */}
                  <div className={styles.formRow}>
                    <div
                      className={`${styles.formColumn} ${styles.cardInvoiceCheckboxColumn}`}
                    >
                      <div className={styles.cardInvoiceCheckboxContainer}>
                        <input
                          type="checkbox"
                          id="wantsInvoiceCard"
                          className={styles.invoiceCheckbox}
                          checked={wantsInvoiceCard}
                          onChange={(e) =>
                            handleWantsInvoiceChange("card", e.target.checked)
                          }
                        />
                        <label
                          htmlFor="wantsInvoiceCard"
                          className={styles.invoiceCheckboxLabel}
                        >
                          {t(
                            "components.paymentModal.cashSection1GenerateInvoice",
                          )}
                        </label>
                      </div>
                    </div>
                    {wantsInvoiceCard && (
                      <div className={styles.formColumn}>
                        <PaymentConfirmPhoneField
                          labelClassName={styles.formLabel}
                          method="card"
                          t={t}
                          countryCodeOptions={countryCodeOptions}
                          phoneCountryCode={phoneCountryCodeCard}
                          confirmPhone={confirmPhoneCard}
                          validationField="confirmPhoneCard"
                          onCountryCodeChange={setPhoneCountryCodeCard}
                          onConfirmPhoneChange={handleConfirmPhoneChange}
                          getFieldValidationState={getFieldValidationState}
                          getErrorMessage={getErrorMessage}
                        />
                      </div>
                    )}
                  </div>

                  <div className={styles.cardImageContainer}>
                    <CardImage className={styles.cardPaymentImage} />
                    <div className={styles.paymentsIconContainer}>
                      <PaymentsIcon className={styles.paymentsIcon} />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
);

export default PaymentModalMethodStep;
